import colors from '@amblique/king-living/app/theme/foundations/colors';

const brandGreyscaleText = colors.brandColors.greyscale;
/** Ghost / placeholder text — matches `brandColors.greyscale80` in foundations/colors.js */
const brandGreyscaleGhost = colors.brandColors.greyscale80;
/** Checkout: placeholder color on address/contact fields */
const CHECKOUT_PLACEHOLDER_COLOR = 'brandColors.greyscale70';
const CHECKOUT_FIELD_TEXT = {
  fontFamily: 'body',
  fontSize: '1rem',
  fontStyle: 'normal',
  fontWeight: 400,
  lineHeight: 1.4,
  fontVariantNumeric: 'lining-nums proportional-nums',
};
const CHECKOUT_INPUT_TEXT = {
  ...CHECKOUT_FIELD_TEXT,
  color: 'greyscale.100',
};
const CHECKOUT_PLACEHOLDER_TEXT = {
  ...CHECKOUT_FIELD_TEXT,
  color: CHECKOUT_PLACEHOLDER_COLOR,
  lineHeight: 1.4,
  opacity: 1,
  textTransform: 'none',
};
/** Overrides baseStyle placeholder `!important` rules */
const CHECKOUT_PLACEHOLDER_SX = {
  ...CHECKOUT_PLACEHOLDER_TEXT,
  fontSize: '1rem !important',
  lineHeight: '1.4 !important',
};
const CHECKOUT_FLOATING_LABEL = {
  color: 'brandColors.primaryDarkLinen',
  fontFamily: 'body',
  fontSize: '0.75rem',
  fontStyle: 'normal',
  fontWeight: 400,
  lineHeight: 1.2,
};
const CHECKOUT_ERROR_MESSAGE = {
  color: 'brandColors.error',
  fontFamily: 'body',
  fontSize: {base: '0.8125rem', lg: '1rem'},
  fontStyle: 'normal',
  fontWeight: 400,
  lineHeight: {base: 1.2, lg: 1.4},
  fontVariantNumeric: 'lining-nums proportional-nums',
};
const CHECKOUT_ERROR_MESSAGE_ICON = {
  color: 'brandColors.error',
};
/** Floating label turns error red when FormControl is invalid (RHF / checkout validation). */
const INVALID_FLOATING_LABEL_SX = {
  '.chakra-form-control[data-invalid] &, .chakra-form-control[data-invalid] input:not(:placeholder-shown) ~ &, .chakra-form-control[data-invalid] input:focus ~ &, .chakra-form-control[data-invalid] textarea:not(:placeholder-shown) ~ &, .chakra-form-control[data-invalid] textarea:focus ~ &':
    {
      color: 'brandColors.error',
    },
};

/**
 * A placeholder that only repeats the label (flagged by `Field` as `data-label-placeholder`) must
 * disappear once the floating label is on screen, or the field reads its own label twice.
 * Focus, disabled and invalid are the states that float the label while the input can still be
 * empty — they mirror the label selectors below.
 * Each vendor pseudo-element needs its own rule: a browser drops the whole selector list when it
 * does not recognise one of them.
 */
const labelDerivedPlaceholderHidden = (pseudoElement) => [
  `&[data-label-placeholder="true"]:focus${pseudoElement}`,
  `&[data-label-placeholder="true"]:disabled${pseudoElement}`,
  `&[data-label-placeholder="true"]:invalid${pseudoElement}`
].join(', ');

const LABEL_DERIVED_PLACEHOLDER_HIDDEN_SX = {
  [labelDerivedPlaceholderHidden('::placeholder')]: {
    color: 'transparent',
  },
  [labelDerivedPlaceholderHidden('::-webkit-input-placeholder')]: {
    color: 'transparent',
  },
  [labelDerivedPlaceholderHidden('::-moz-placeholder')]: {
    color: 'transparent',
    opacity: 1,
  },
};

/**
 * A select paints its placeholder as the selected `<option>`, not as `::placeholder`, so it has to
 * be styled through the control's own colour and padding. `Field` sets `data-placeholder-shown`
 * while nothing is selected; these rules then make the select read like a resting text input —
 * same padding, same ghost colour — instead of an empty box under a floating label.
 * `& option` keeps the dropdown list itself at full contrast, since options inherit the control.
 */
const selectPlaceholderShownSx = (placeholderColor) => ({
  '&[data-placeholder-shown="true"]': {
    // No vertical padding: a select centres its text in the content box, and matching the inputs'
    // 18px padding would leave that box shorter than the line height, clipping descenders.
    pt: 0,
    pb: 0,
    color: placeholderColor,
  },
  '& option': {
    color: brandGreyscaleText,
  },
});

/** Shared row height for text `Input`, `Select`, and adjacent icon slots */
const FIELD_INPUT_HEIGHT = 13.5;

const Field = {
  parts: [
    'inputGroup',
    'inputGroupAutocompleteAddress',
    'input',
    'formErrorMessage',
    'formErrorMessageIcon',
    'label',
    'labelSelect',
    'passwordIconButtonContainer',
    'passwordIconButton',
    'passwordIcon',
    'clearButtonContainer',
    'clearButton',
    'clearIcon',
    'checkbox',
    'select',
    'textarea',
    'fileInput',
    'fileInputContainer',
    'fileInputIcon',
    'fileInputPrimaryText',
    'fileInputSecondaryText',
    'fileInputErrorAlert',
    'fileInputErrorAlertIcon'
  ],
  baseStyle: {
    inputGroup: {
      gap: 2,
      position: 'relative',
    },
    inputGroupAutocompleteAddress: {
      zIndex: 1100, // Higher than most overlays to ensure autocomplete dropdown is visible
      position: 'relative',
    },
    input: {
      height: FIELD_INPUT_HEIGHT,
      px: 4.5,
      py: 0,
      pt: 4.5,
      pb: 4.5,
      alignItems: 'center',
      gap: 2.5,
      alignSelf: 'stretch',
      borderRadius: 'base',
      border: '1px solid',
      borderColor: 'brandColors.greyscale10',
      backgroundColor: 'white',
      color: 'brandColors.greyscale',
      fontSize: 'sm',
      fontStyle: 'normal',
      fontWeight: 400,
      lineHeight: 1.2,
      fontFamily: 'body',
      transition: 'all 0.2s ease-in-out',
      _focus: {
        backgroundColor: 'white',
        borderWidth: '1.5px',
        borderColor: 'brandColors.primaryDarkLinen',
        boxShadow: 'none',
        outline: 'none',
        pt: 6.875,
        pb: 2.375,
      },
      sx: {
        '&:not(:placeholder-shown)': {
          pt: 6.875,
          pb: 2.375,
        },
        '.chakra-input__group:has(.field__password-icon-container) &::placeholder': {
          color: 'var(--chakra-colors-brandColors-greyscale70)',
          opacity: 1,
        },
        '&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus, &:-webkit-autofill:active': {
          WebkitTextFillColor: brandGreyscaleText,
        },
        '&:autofill': {
          color: brandGreyscaleText,
        },
        '&::placeholder, &::-webkit-input-placeholder': {
          color: brandGreyscaleGhost,
          fontSize: 'sm',
          lineHeight: '120% !important',
          opacity: 1,
          textTransform: 'none',
        },
        ...LABEL_DERIVED_PLACEHOLDER_HIDDEN_SX,
      },
      _hover: {
        borderColor: 'brandColors.primaryDarkLinen',
        backgroundColor: 'white',
      },
      _invalid: {
        borderWidth: '1px',
        borderColor: 'brandColors.error',
        backgroundColor: 'brandColors.errorBG',
        _focus: {
          borderColor: 'brandColors.error',
          backgroundColor: 'brandColors.errorBG',
        },
      },
      _disabled: {
        backgroundColor: 'whiteAlpha.650',
        borderWidth: '1.5px',
        borderColor: 'brandColors.greyscale10',
        color: 'brandColors.greyscale60',
        cursor: 'not-allowed',
        opacity: 1,
        paddingTop: 6.875,
        paddingBottom: 2.375,
      },
      _placeholder: {
        color: 'brandColors.greyscale80',
        lineHeight: '120% !important',
        opacity: 1,
        textTransform: 'none',
      },
    },
    formErrorMessage: {
      mt: 2,
      px: 4.5,
      color: '#9D2A1E',
      fontFamily: 'body',
      fontSize: '12px',
      fontStyle: 'normal',
      fontWeight: 400,
      lineHeight: 'shorter',
      display: 'flex',
      alignItems: 'center',
      gap: 2,
    },
    formErrorMessageIcon: {
      width: 3,
      height: 3,
      color: '#9D2A1E',
      flexShrink: 0,
    },
    label: {
      position: 'absolute',
      top: 4.5,
      left: 4.5,
      color: 'brandColors.greyscale80',
      fontSize: 'xs',
      fontStyle: 'normal',
      fontWeight: 'normal',
      lineHeight: 'shorter',
      fontFamily: 'body',
      textTransform: 'none',
      transition: 'all 0.2s ease-in-out',
      cursor: 'text',
      zIndex: 1,
      pointerEvents: 'none',
      opacity: 0, // Hidden in normal state
      sx: {
        'input:not(:placeholder-shown) ~ &, input:focus ~ &, textarea:not(:placeholder-shown) ~ &, textarea:focus ~ &': {
          opacity: 1, // Show label when focused or has value
          top: 2,
          color: 'brandColors.primaryDarkLinen',
          fontSize: 'xs',
          fontWeight: 400,
          lineHeight: 'shorter',
        },
        'input:invalid ~ &, textarea:invalid ~ &': {
          opacity: 1, // Show label when invalid
          color: 'brandColors.error',
        },
        'input:disabled ~ &, textarea:disabled ~ &': {
          opacity: 1, // Show label even when disabled
          top: 2, // Same position as focus state
          color: 'brandColors.primaryDarkLinen',
          fontSize: 'xs',
          fontWeight: 400,
          lineHeight: 'shorter',
        },
        ...INVALID_FLOATING_LABEL_SX,
      },
    },
    labelSelect: {
      position: 'absolute',
      top: 2,
      left: 4.5,
      color: 'brandColors.primaryDarkLinen',
      fontSize: 'xs',
      fontStyle: 'normal',
      fontWeight: 'normal',
      lineHeight: 'shorter',
      fontFamily: 'body',
      textTransform: 'none',
      opacity: 1,
      pointerEvents: 'none',
      zIndex: 1,
    },
    passwordIconButtonContainer: {
      height: FIELD_INPUT_HEIGHT,
      width: 13,
    },
    passwordIconButton: {
      height: 'full',
      width: 'full',
    },
    passwordIcon: {
      width: 5, // 20px
      height: 5, // 20px
      color: 'brandColors.primaryDarkLinen',
    },
    clearButtonContainer: {
      height: FIELD_INPUT_HEIGHT,
      width: 13,
      position: 'absolute',
      right: 0,
      top: 0,
    },
    clearButton: {
      height: 'full',
      width: 'full',
      minWidth: 'auto',
      padding: 0,
      borderRadius: 0,
      backgroundColor: 'transparent',
      _hover: {
        backgroundColor: 'transparent',
      },
      _active: {
        backgroundColor: 'transparent',
      },
    },
    clearIcon: {
      width: 5, // 20px
      height: 5, // 20px
      color: 'brandColors.primaryDarkLinen',
    },
    checkbox: {},
    select: {
      height: FIELD_INPUT_HEIGHT,
      padding: 0,
      borderRadius: 'base',
      border: '1px solid',
      borderColor: 'brandColors.greyscale10',
      backgroundColor: 'white',
      color: 'brandColors.greyscale',
      // KL-1088: value font reduced from md (16px) to sm (14px) to match the text
      // inputs and stop the copy being clipped. Horizontal/vertical padding is
      // handled by the Field component's inline sx (pt/pb) + native select padding,
      // so it must NOT be overridden here (doing so indents/misaligns the value).
      fontSize: 'sm',
      fontStyle: 'normal',
      fontWeight: 'normal',
      lineHeight: 'shorter',
      transition: 'all 0.2s ease-in-out',
      _focus: {
        backgroundColor: 'white',
        borderWidth: '1.5px',
        borderColor: 'brandColors.primaryDarkLinen',
        boxShadow: 'none',
        outline: 'none',
      },
      _hover: {
        borderColor: 'brandColors.greyscale10',
        backgroundColor: 'white',
      },
      _invalid: {
        borderWidth: '1px',
        borderColor: 'brandColors.error',
        backgroundColor: 'brandColors.errorBG',
        _focus: {
          borderColor: 'brandColors.error',
          backgroundColor: 'brandColors.errorBG',
        },
      },
      _disabled: {
        backgroundColor: 'whiteAlpha.650',
        borderWidth: '1.5px',
        borderColor: 'brandColors.greyscale10',
        color: 'brandColors.greyscale60',
        cursor: 'not-allowed',
        opacity: 1,
      },
      _placeholder: {
        color: 'brandColors.greyscale80',
        lineHeight: 1.2,
        opacity: 1,
        textTransform: 'none',
      },
      sx: {
        '&::placeholder, &::-webkit-input-placeholder': {
          color: brandGreyscaleGhost,
          fontSize: 'sm',
          lineHeight: '120% !important',
          opacity: 1,
          textTransform: 'none',
        },
        ...selectPlaceholderShownSx(brandGreyscaleGhost),
      },
    },
    textarea: {
      minH: '120px', // Minimum height for textarea
      px: 4.5, // 18px
      py: 4.5, // 18px
      borderRadius: 'base',
      border: '1px solid',
      borderColor: 'brandColors.greyscale10',
      backgroundColor: 'white',
      color: 'brandColors.greyscale',
      fontSize: 'sm',
      fontStyle: 'normal',
      fontWeight: 'normal',
      lineHeight: 'shorter',
      fontFamily: 'body',
      transition: 'all 0.2s ease-in-out',
      resize: 'vertical', // Allow vertical resizing
      _focus: {
        backgroundColor: 'white',
        borderWidth: '1.5px',
        borderColor: 'brandColors.primaryDarkLinen',
        boxShadow: 'none',
        outline: 'none',
      },
      _hover: {
        borderColor: 'brandColors.greyscale10',
        backgroundColor: 'white',
      },
      _invalid: {
        borderWidth: '1px',
        borderColor: 'brandColors.error',
        backgroundColor: 'brandColors.errorBG',
        _focus: {
          borderColor: 'brandColors.error',
          backgroundColor: 'brandColors.errorBG',
        },
      },
      _disabled: {
        backgroundColor: 'whiteAlpha.650',
        borderWidth: '1.5px',
        borderColor: 'brandColors.greyscale10',
        color: 'brandColors.greyscale60',
        cursor: 'not-allowed',
        opacity: 1,
      },
      _placeholder: {
        color: 'brandColors.greyscale80',
        lineHeight: '120% !important',
        opacity: 1,
        textTransform: 'none',
      },
      sx: {
        '&::placeholder, &::-webkit-input-placeholder': {
          color: brandGreyscaleGhost,
          fontSize: 'sm',
          lineHeight: '120% !important',
          opacity: 1,
          textTransform: 'none',
        },
        ...LABEL_DERIVED_PLACEHOLDER_HIDDEN_SX,
      },
    },
    fileInput: {
      // Hidden native file input
      position: 'absolute',
      opacity: 0,
      width: 0,
      height: 0,
      overflow: 'hidden',
    },
    fileInputContainer: {
      // Container for the custom file input UI
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 3, // 12px
      px: 31, // 124px (31 * 4 = 124px)
      py: 6, // 24px (6 * 4 = 24px)
      borderRadius: 'base',
      border: '2px dashed',
      borderColor: 'brandColors.greyscale20', // rgba(27,26,24,0.2) - always 2px to keep size consistent
      backgroundColor: 'white',
      cursor: 'pointer',
      transition: 'all 0.2s ease-in-out',
      boxSizing: 'border-box', // Ensure border is included in size calculation
      position: 'relative', // For positioning context
      _hover: {
        borderColor: 'brandColors.primaryLinen100', // #c6b5a1
        backgroundColor: 'brandColors.greyscale10', // rgba(27,26,24,0.1)
      },
    },
    fileInputIcon: {
      width: 5, // 20px
      height: 5, // 20px
      color: 'brandColors.greyscale90', // rgba(50,49,47,0.9) or #1c1c1c
      flexShrink: 0,
    },
    fileInputPrimaryText: {
      fontSize: 'md', // 16px
      fontWeight: '600', // SemiBold
      lineHeight: '140%', // 1.4
      color: 'brandColors.greyscale90', // rgba(50,49,47,0.9) or #1c1c1c
      textAlign: 'center',
      fontFamily: 'body',
    },
    fileInputSecondaryText: {
      fontSize: 'md', // 16px
      fontWeight: 'normal', // Regular
      lineHeight: '140%', // 1.4
      color: 'brandColors.greyscale90', // rgba(50,49,47,0.9) or #1c1c1c
      textAlign: 'center',
      fontFamily: 'body',
    },
    fileInputErrorAlert: {
      // Error alert below file input container
      mt: 3, // 12px gap
      px: 4, // 16px
      py: 1, // 4px
      borderRadius: 'base',
      border: '1px solid',
      borderColor: 'brandColors.error', // #9d2a1e
      backgroundColor: 'brandColors.errorBG', // #fef2f2
      display: 'flex',
      alignItems: 'center',
      gap: 2, // 8px
      fontSize: 'xs',
      fontFamily: 'body',
      fontWeight: 'normal',
      lineHeight: 'shorter', // 1.2
      color: 'brandColors.greyscale90', // rgba(27,26,24,0.9)
    },
    fileInputErrorAlertIcon: {
      width: 5, // 20px
      height: 5, // 20px
      color: 'brandColors.error', // #9d2a1e - match border color
      flexShrink: 0,
    },
  },
  variants: {
    success: {
      input: {
        backgroundColor: 'brandColors.secondaryBG',
        borderWidth: '1.5px',
        borderColor: 'brandColors.greyscale10',
      },
    },
    /** Auth forms (e.g. reset password): 14px value + placeholder — matches Figma */
    authForm: {
      input: {
        fontSize: 'sm',
        color: 'brandColors.greyscale',
        sx: {
          '&:not(:placeholder-shown)': {
            pt: 6.875,
            pb: 2.375,
          },
          '&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus, &:-webkit-autofill:active': {
            WebkitTextFillColor: brandGreyscaleText,
          },
          '&:autofill': {
            color: brandGreyscaleText,
          },
          '&::placeholder, &::-webkit-input-placeholder': {
            color: brandGreyscaleGhost,
            fontSize: 'sm',
            lineHeight: '120% !important',
            opacity: 1,
            textTransform: 'none',
          },
        },
      },
    },
    /** Checkout: address/contact inputs — body default value + 70% placeholder */
    checkout: {
      input: {
        ...CHECKOUT_INPUT_TEXT,
        sx: {
          '&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus, &:-webkit-autofill:active': {
            WebkitTextFillColor: colors.greyscale[100],
          },
          '&:autofill': {
            color: colors.greyscale[100],
          },
          '&::placeholder, &::-webkit-input-placeholder': CHECKOUT_PLACEHOLDER_SX,
          '.chakra-input__group:has(.field__password-icon-container) &::placeholder': {
            color: CHECKOUT_PLACEHOLDER_COLOR,
            opacity: 1,
          },
          ...LABEL_DERIVED_PLACEHOLDER_HIDDEN_SX,
        },
        _placeholder: CHECKOUT_PLACEHOLDER_SX,
      },
      select: {
        ...CHECKOUT_INPUT_TEXT,
        _placeholder: CHECKOUT_PLACEHOLDER_SX,
        sx: {
          pt: '24px',
          pb: '5px',
          '&::placeholder, &::-webkit-input-placeholder': CHECKOUT_PLACEHOLDER_SX,
          ...selectPlaceholderShownSx(CHECKOUT_PLACEHOLDER_COLOR),
        },
      },
      textarea: {
        minH: '101px',
        px: 4.5,
        pt: 2.375,
        pb: 2.375,
        resize: 'vertical',
        ...CHECKOUT_INPUT_TEXT,
        _focus: {
          backgroundColor: 'white',
          borderWidth: '1.5px',
          borderColor: 'brandColors.primaryDarkLinen',
          boxShadow: 'none',
          outline: 'none',
          pt: 6.875,
          pb: 2.375,
        },
        sx: {
          '&:not(:placeholder-shown)': {
            pt: 6.875,
            pb: 2.375,
          },
          '&:placeholder-shown:not(:focus)': {
            pt: 2.375,
            pb: 2.375,
          },
          '&::placeholder, &::-webkit-input-placeholder': CHECKOUT_PLACEHOLDER_SX,
          ...LABEL_DERIVED_PLACEHOLDER_HIDDEN_SX,
        },
        _placeholder: CHECKOUT_PLACEHOLDER_SX,
      },
      label: {
        sx: {
          'input:not(:placeholder-shown) ~ &, input:focus ~ &, textarea:not(:placeholder-shown) ~ &, textarea:focus ~ &': {
            ...CHECKOUT_FLOATING_LABEL,
            opacity: 1,
            top: 2,
          },
          'input:invalid ~ &, textarea:invalid ~ &': {
            color: 'brandColors.error',
            opacity: 1,
            top: 2,
          },
          'input:disabled ~ &, textarea:disabled ~ &': {
            ...CHECKOUT_FLOATING_LABEL,
            opacity: 1,
            top: 2,
          },
          ...INVALID_FLOATING_LABEL_SX,
        },
      },
      labelSelect: {
        ...CHECKOUT_FLOATING_LABEL,
        sx: INVALID_FLOATING_LABEL_SX,
      },
      formErrorMessage: CHECKOUT_ERROR_MESSAGE,
      formErrorMessageIcon: CHECKOUT_ERROR_MESSAGE_ICON,
    },
    /** Checkout payment card fields — empty: placeholder (3339:9981); filled: floating label (3339:9167) */
    checkoutPayment: {
      label: {
        left: '16px',
        color: 'brandColors.primaryDarkLinen',
        fontSize: 'xs',
        fontWeight: 400,
        lineHeight: 1.2,
        opacity: 0,
        pointerEvents: 'none',
        position: 'absolute',
        top: '18px',
        transition: 'all 0.2s ease-in-out',
        zIndex: 1,
        sx: {
          'input:not(:placeholder-shown) ~ &, input:focus ~ &': {
            color: 'brandColors.primaryDarkLinen',
            fontSize: 'xs',
            fontWeight: 400,
            left: '16px',
            lineHeight: 1.2,
            opacity: 1,
            top: '4px',
          },
          ...INVALID_FLOATING_LABEL_SX,
        },
      },
      input: {
        height: '55px',
        px: '16px',
        pt: '16px',
        pb: '16px',
        fontSize: {base: '14px', lg: '16px'},
        lineHeight: 1.4,
        color: 'greyscale.100',
        borderColor: 'grayAlpha.10',
        borderRadius: 'base',
        _focus: {
          pb: '4px',
          pt: '22px',
          borderWidth: '1px',
          borderColor: 'grayAlpha.10',
          boxShadow: 'none',
          backgroundColor: 'white',
        },
        _hover: {
          borderColor: 'grayAlpha.10',
          backgroundColor: 'white',
        },
        sx: {
          '&:not(:placeholder-shown)': {
            pb: '4px',
            pt: '22px',
          },
          '&:placeholder-shown:not(:focus)': {
            pb: '16px',
            pt: '16px',
          },
          '&::placeholder, &::-webkit-input-placeholder': {
            color: 'greyscale90Alpha.70',
            fontSize: {base: '14px', lg: '16px'},
            lineHeight: 1.4,
            opacity: 1,
            textTransform: 'none',
          },
        },
      },
      passwordIconButtonContainer: {
        alignItems: 'center',
        height: '100%',
        justifyContent: 'center',
        width: '48px',
      },
      passwordIconButton: {
        h: '20px',
        minW: '20px',
        w: '20px',
      },
      passwordIcon: {
        boxSize: '20px',
      },
      formErrorMessage: CHECKOUT_ERROR_MESSAGE,
      formErrorMessageIcon: CHECKOUT_ERROR_MESSAGE_ICON,
    },
    fileError: {
      fileInputContainer: {
        borderWidth: '1px',
        borderColor: 'brandColors.greyscale10', // rgba(27,26,24,0.1)
        backgroundColor: 'white',
        px: 0, // 0px horizontal padding in error state
      },
    },
  },
};

export default Field;
